import React, { useCallback, useEffect, useRef, useState } from "react";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { FilterChangedEvent } from "ag-grid-community";
import { trackPromise } from "react-promise-tracker";
import { Button } from "@material-ui/core";

import { api } from "../../../../../utils/api";
import { PaginationTypes } from "../../../../../@types/pagination.types";
import { CustomPagination } from "../../../../shared";
import { handleValueCellRenderer } from "../../../../../utils/commonFunctions/cellRenderers";
import { CustomFloatingFilter } from "../../../../shared";
import { CustomFilter } from "../../../../shared";
import { ProvidersDetails } from "../../../../../@types/components/provider.types";
import { dateTimeZoneConverter } from "../../../../../utils/commonFunctions/date";
import useRouteParam from "../../../../../hooks/useRouteParam";
import encryption from "../../../../../utils/commonFunctions/encryption";
import { useNavigate } from "react-router-dom";
import { group } from "../../../../../utils/api/admin/group/group";

const ProvidersGrid: React.FC = (): JSX.Element => {
	const [rowData, setRowData] = useState<ProvidersDetails[]>([]);
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const [pagination, setPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const [groupProvidersFilterSchema, setGroupProvidersFilterSchema] = useState({
		tax_id: {
			value: null,
			type: null
		},
		first_name: {
			value: null,
			type: null
		},
		last_name: {
			value: null,
			type: null
		},
		degree: {
			value: null,
			type: null
		}
	});

	const queryGroupNumber = useRouteParam("gn");
	// console.log("group_number_from_query", queryGroupNumber);
	const agGridRef = useRef<any>(null);
	const navigate = useNavigate();

	const handleFilterChanged = useCallback((event: FilterChangedEvent) => {
		if (event && event.api && event.api.getFilterModel) {
			const filterModel = event.api.getFilterModel();
			const taxIdFilter = filterModel["tax_id"];
			// console.log("Filter Model:", filterModel); // Log the entire filter model to inspect its structure
			if (taxIdFilter) {
				const { type, filter } = taxIdFilter;
				// console.log(`Filter changed: Type - ${type}, Value - ${filter}`);
			}
		}
	}, []);

	const handleDateCellRenderer = useCallback(({ value }: { value: string }) => dateTimeZoneConverter(value), []);

	const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
		setPagination((prevPagination: any) => ({
			...prevPagination,
			currentPage: page
		}));
	}, []);

	const handleAgGridReady = (params: GridReadyEvent) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const handleRedirectionProviderDetailsLink = useCallback(
		(queryGroupNumber: number) => {
			const focusedCell = agGridRef.current.api.getFocusedCell();
			const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);

			const {
				data: { tax_id }
			} = row;

			const redirectionLink = `/employer/provider-details?gn=${queryGroupNumber}&&tx=${
				encryption(tax_id) as string
			}`;

			navigate(redirectionLink);
		},
		[navigate]
	);

	const handleActionDetailsButtonRender = useCallback(
		({ queryGroupNumber }: { queryGroupNumber: number }) => {
			return (
				<Button
					className="ag-grid-cell-button"
					variant="outlined"
					onClick={() => handleRedirectionProviderDetailsLink(queryGroupNumber)}
					style={{ marginTop: "3px", marginBottom: "6px" }}
				>
					See Details
				</Button>
			);
		},
		[handleRedirectionProviderDetailsLink]
	);

	const handleGetProviderData = useCallback(async () => {
		const filter = {
			page: pagination.currentPage, // Pass the current page from state
			tax_id: groupProvidersFilterSchema.tax_id.value, // Add tax_id filter value from state
			first_name: groupProvidersFilterSchema.first_name.value,
			last_name: groupProvidersFilterSchema.last_name.value,
			degree: groupProvidersFilterSchema.degree.value
		};
		const response = await trackPromise(api.providers.getProvidersData(filter)); // Pass filter to API call
		const { providerList, pagination: responsePagination } = response;
		// console.log("Providers List => ", providerList);
		if (response) {
			setRowData(providerList);
			setPagination((prevPagination: any) => ({
				...prevPagination,
				pageCount: responsePagination.totalPages
			}));
		}
		// console.log("Filter sent to backend:", filter);
	}, [
		pagination.currentPage,
		groupProvidersFilterSchema.tax_id.value,
		groupProvidersFilterSchema.first_name.value,
		groupProvidersFilterSchema.last_name.value,
		groupProvidersFilterSchema.degree.value
	]);

	useEffect(() => {
		handleGetProviderData();
	}, [handleGetProviderData]);

	return (
		<div className="all-provider-grid-container">
			<div className="ag-theme-alpine" style={{ height: "400px", width: "100%" }}>
				<AgGridReact
					ref={agGridRef}
					rowData={rowData}
					onGridReady={handleAgGridReady}
					onFirstDataRendered={handleFirstDataRendered}
					rowSelection={"Single"}
					animateRows
					suppressColumnVirtualisation={true}
					defaultColDef={{
						filter: "agTextColumnFilter",
						floatingFilter: true,
						resizable: true
					}}
					enableCellChangeFlash={true}
					defaultColGroupDef={{ marryChildren: true }}
					frameworkComponents={{
						schema: groupProvidersFilterSchema,
						setPayloadSchema: setGroupProvidersFilterSchema,
						handleValueCellRenderer: handleValueCellRenderer,
						handleDateCellRenderer: handleDateCellRenderer,
						handleActionDetailsButtonRender: handleActionDetailsButtonRender,
						customFloatingFilter: CustomFloatingFilter,
						customFilter: CustomFilter
					}}
					onFilterChanged={handleFilterChanged}
				>
					<AgGridColumn
						field="tax_id"
						headerName="Tax Id"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						onCellClicked={() => handleRedirectionProviderDetailsLink(queryGroupNumber as number)}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
					></AgGridColumn>
					<AgGridColumn
						field="first_name"
						headerName="First Name"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						onCellClicked={() => handleRedirectionProviderDetailsLink(queryGroupNumber as number)}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						floatingFilterComponent="customFloatingFilter"
						filter="customFilter"
					></AgGridColumn>
					<AgGridColumn
						field="last_name"
						headerName="Last name"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => handleRedirectionProviderDetailsLink(queryGroupNumber as number)}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					></AgGridColumn>
					<AgGridColumn
						field="degree"
						headerName="Degree"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					></AgGridColumn>
					{/* <AgGridColumn
						field="group_or_facility_name"
						headerName="Group or Facity Name"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					></AgGridColumn> */}
					<AgGridColumn
						field="speciality"
						headerName="Speciality"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="address"
						headerName="Address"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="city"
						headerName="City"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="state"
						headerName="State"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="ZIP"
						headerName="ZIP"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="phone_number"
						headerName="Phone Number"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="fax_number"
						headerName="Fax Number"
						cellRenderer="handleValueCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="effective_date"
						headerName="Effective Date"
						cellRenderer="handleDateCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="termination_date"
						headerName="Termination Date"
						cellRenderer="handleDateCellRenderer"
						suppressSizeToFit
						floatingFilterComponent="customFloatingFilter"
						onCellClicked={() => null}
						cellStyle={() => {
							return { cursor: "pointer" };
						}}
						filter="customFilter"
					/>
					<AgGridColumn
						field="action"
						headerName="See Details"
						cellRenderer="handleActionDetailsButtonRender"
						cellRendererParams={{ queryGroupNumber }}
						suppressSizeToFit={true}
						filter={false}
					/>
				</AgGridReact>
			</div>
			<CustomPagination
				currentPage={pagination.currentPage}
				handlePageChange={handlePageChange}
				pageCount={pagination.pageCount}
				key={2}
			/>
		</div>
	);
};

export default ProvidersGrid;
